import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { Box, Button, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import ListsAndCards from 'src/components/Contacts/ListAndCards';
import TopSectionBoards from 'src/components/boardsAndLists/TopSectionBoards';
import { BoardProvider } from 'src/context/BoardContext';
import { getAllBoardLists, getBoardDetailsById } from 'src/services/boards';
import { IListItem, ILocationState } from 'src/types/boards-lists';

export const Boards = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { board_id } = useParams();
  const state = location.state as ILocationState | undefined;
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [listsLoading, setListsLoading] = useState(false);
  const [lists, setLists] = useState<IListItem[]>([]);

  const [boardName, setBoardName] = useState<string>(state?.board_name ?? '');
  const [boardDescription, setBoardDescription] = useState<string>(
    state?.board_description ?? ''
  );

  const isInitialLoading = loading || listsLoading;

  const styles = {
    wrapper: {
      color: theme.palette.mode === 'dark' ? 'grey.100' : 'text.primary',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      transition: 'all 0.3s ease',
      p: { xs: 1, sm: 1 },
    },
    contentContainer: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'relative',
      height: '100%',
      p: 2,
    },
    contentWrapper: {
      position: 'relative',
      borderRadius: '8px',
      border: `1px solid ${
        theme.palette.mode === 'dark'
          ? theme.palette.grey[800]
          : theme.palette.grey[200]
      }`,
      overflow: 'hidden',
      boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      background: theme.palette.background.paper,
      backdropFilter: 'blur(8px)',
      p: 2,
    },
  };

  const fetchBoardDetails = async () => {
    try {
      setLoading(true);
      const response = await getBoardDetailsById(board_id);
      const { board } = response;
      const { title, description } = board;

      setBoardName(title || 'No title available');
      setBoardDescription(description || 'No description available');
    } catch (error) {
      console.error('Erro ao buscar detalhes do board:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchList = async () => {
    try {
      setListsLoading(true);
      const response = await getAllBoardLists(board_id);
      setLists(response.lists || []);
    } catch (error) {
      console.error('Erro ao buscar listas:', error);
    } finally {
      setListsLoading(false);
    }
  };

  useEffect(() => {
    if (!board_id) {
      navigate('/boards-lists');
      return;
    }

    fetchList();

    if (!state?.board_name) {
      fetchBoardDetails();
    } else {
      setBoardName(state.board_name);
      setBoardDescription(state.board_description);
    }
  }, [board_id]);

  const isSmallScreen =
    useTheme().breakpoints.values.lg > window.innerWidth ||
    window.innerWidth < 1500;

  return (
    <BoardProvider
      lists={lists}
      boardId={board_id}
      setLists={setLists}
      fetchList={fetchList}
    >
      <Box sx={styles.wrapper}>
        <Box sx={styles.contentContainer}>
          <Box sx={styles.contentWrapper}>
            <TopSectionBoards
              title={boardName || 'Loading...'}
              description={boardDescription}
              loading={isInitialLoading}
              leftAction={
                <Button
                  onClick={() => navigate(-1)}
                  sx={{
                    p: 1,
                    minWidth: 0,
                    color: theme.palette.text.primary,
                  }}
                >
                  <ArrowBackIosIcon sx={{ fontSize: '1.1rem', mb: 0.1 }} />
                </Button>
              }
            />

            <ListsAndCards boardMode="boards" boardId={board_id} />
          </Box>
        </Box>
      </Box>
    </BoardProvider>
  );
};
